/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useRef, useMemo } from 'react'
import { DragDropProvider } from '@dnd-kit/react'
import {
    PointerSensor,
    PointerActivationConstraints,
    AutoScroller,
} from '@dnd-kit/dom'
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers'
import {
    prettyStatNames,
    prettyStatNamesMobile,
    PITCH_ARSENAL_STATS,
} from './utils/stat-groupings.ts'
import { PlayerRow, StatColumnProps } from './utils/types.ts'
import RemoveButton from './RemoveButton.tsx'
import SortableWrapper from './SortableWrapper.tsx'
import PitchPillElement from './PitchPillElement.tsx'

import {
    LINE_HEIGHT,
    TEXT_Y_OFFSET,
    SIDE_BUFFER,
    // TOP_BUFFER,
    SECTION_MARGIN,
    HEADER_TEXT_HEIGHT,
    TEAM_LOGO_SIZE,
    // TEXT_RIGHT_SIDE,
} from './utils/constants.ts'
import { useDimensions } from './utils/responsive-dimensions.tsx'

function getLabelToUse(
    strippedKey: string,
    isMobile: boolean,
    currentType: 'batter' | 'pitcher'
) {
    const mapToUse = isMobile ? prettyStatNamesMobile : prettyStatNames

    return currentType === 'pitcher' &&
        strippedKey === 'percent_rank_swing_take_run_value'
        ? 'Pitching'
        : mapToUse[strippedKey]
}

function StatColumn(props: StatColumnProps) {
    const {
        groupedSelects,
        currentType,
        existingTypes,
        onRemoveStat,
        onStatReorder,
        selectedStats,
        showTeams,
        winnerStats,
        onAddWinnerStat,
    } = props
    const { TEXT_RIGHT_SIDE, TOP_BUFFER, isMobile } = useDimensions()

    const [hoveredStat, setHoveredStat] = useState<string | null>(null)
    const groupRefs = useRef<Record<string, SVGGElement | null>>({})

    const sensors = useMemo(
        () => [
            PointerSensor.configure({
                activationConstraints: [
                    new PointerActivationConstraints.Distance({ value: 8 }),
                    new PointerActivationConstraints.Delay({
                        value: isMobile ? 800 : 200,
                        tolerance: isMobile ? 5 : 10,
                    }),
                ],
            }),
        ],
        []
    )

    let numStatsSeen = 0

    const statGroupings = existingTypes.map((type, groupingsSeen) => {
        const statLabels = groupedSelects[type].map((strippedKey, i) => {
            const globalIndex = selectedStats.indexOf(strippedKey)
            const yVal = i * LINE_HEIGHT

            let arsenalPitchType
            let arsenalPitchStat

            const isArsenal = PITCH_ARSENAL_STATS.has(strippedKey)

            if (isArsenal) {
                ;[arsenalPitchType, arsenalPitchStat] =
                    strippedKey.split('_percent_rank_')
            }

            const isWinnerStat = winnerStats?.includes(
                strippedKey as keyof PlayerRow
            )

            return (
                <SortableWrapper
                    key={strippedKey}
                    id={strippedKey}
                    index={globalIndex}
                    groupName={`stats-${type}`} // Unique group name to prevent cross-group dropping
                    offset={yVal}
                    container={groupRefs.current[type]}
                    type="stat"
                >
                    <g
                        onMouseEnter={() => setHoveredStat(strippedKey)}
                        onMouseLeave={() => setHoveredStat(null)}
                        style={{ cursor: 'pointer' }}
                    >
                        <rect
                            x={-55} // Extends far enough to the left to cover the X button
                            y={0}
                            width={TEXT_RIGHT_SIDE + 40} // Covers the full width of the label area
                            height={LINE_HEIGHT}
                            fill="transparent"
                        />
                        {isArsenal && (
                            <PitchPillElement
                                x={TEXT_RIGHT_SIDE + 4}
                                y={TEXT_Y_OFFSET}
                                pitchType={arsenalPitchType!}
                                statName={arsenalPitchStat!}
                                flipOrder
                                textClass="label-text"
                                rightJustify
                            />
                        )}
                        {!isArsenal && (
                            <text
                                x={TEXT_RIGHT_SIDE}
                                y={TEXT_Y_OFFSET}
                                className="label-text"
                                style={{ fontFamily: 'Roboto Condensed' }}
                            >
                                {getLabelToUse(
                                    strippedKey,
                                    isMobile,
                                    currentType
                                )}
                            </text>
                        )}
                        <text
                            x={isMobile ? -79 : -56}
                            y={12}
                            fill={isWinnerStat ? '#FFD700' : '#a0a0a0'}
                            style={{
                                opacity: hoveredStat === strippedKey ? 1 : 0,
                                pointerEvents:
                                    hoveredStat === strippedKey || isWinnerStat
                                        ? 'auto'
                                        : 'none',
                            }}
                            className="winner-button-star-elem"
                            onClick={(e) => {
                                e.stopPropagation()
                                if (onAddWinnerStat) {
                                    onAddWinnerStat(strippedKey)
                                }
                            }}
                        >
                            {isWinnerStat ? '★' : '☆'}
                        </text>
                        <g
                            onClick={(e) => {
                                e.stopPropagation()
                                onRemoveStat(strippedKey)
                            }}
                            style={{
                                opacity: hoveredStat === strippedKey ? 1 : 0,
                                pointerEvents:
                                    hoveredStat === strippedKey
                                        ? 'auto'
                                        : 'none',
                            }}
                            className="remove-button-x-elem"
                        >
                            <RemoveButton
                                x={isMobile ? -57 : -30}
                                y={TEXT_Y_OFFSET + 5}
                                symbol="×"
                            />
                        </g>
                    </g>
                </SortableWrapper>
            )
        })

        // const headersOffset = STAT_HEADER_GAP * (groupingsSeen + 1)
        const headersOffset =
            (groupingsSeen + 1) * SECTION_MARGIN +
            (groupingsSeen + 1) * HEADER_TEXT_HEIGHT
        const statsOffset = numStatsSeen * LINE_HEIGHT
        const teamOffset = showTeams ? 0 : -TEAM_LOGO_SIZE
        numStatsSeen += groupedSelects[type].length

        const labels = (
            <g
                ref={(el) => {
                    groupRefs.current[type] = el
                }}
                transform={`translate(0,${
                    headersOffset + statsOffset + teamOffset
                })`}
                key={`${type}_stat_grouping`}
            >
                {statLabels}
            </g>
        )

        return labels
    })

    return (
        <DragDropProvider
            sensors={sensors}
            onDragOver={onStatReorder}
            modifiers={[RestrictToVerticalAxis]}
            plugins={(defaults) =>
                defaults.filter((plugin) => plugin !== AutoScroller)
            }
        >
            <g transform={`translate(${SIDE_BUFFER}, ${TOP_BUFFER})`}>
                {statGroupings}
            </g>
        </DragDropProvider>
    )
}

export default StatColumn
